/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {useIntl} from 'react-intl'
import {validatePassword} from '@salesforce/retail-react-app/app/utils/password-utils'

export default function useUpdatePasswordFields({
    form: {
        control,
        formState: {errors}
    },
    prefix = ''
}) {
    const {formatMessage} = useIntl()

    const fields = {
        email: {
            name: `${prefix}email`,
            label: formatMessage({defaultMessage: 'Email', id: 'use_profile_fields.label.email'}),
            placeholder: 'you@email.com',
            type: 'email',
            defaultValue: '',
            autoComplete: 'email',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter a valid email address.',
                    id: 'use_profile_fields.error.required_email'
                })
            },
            error: errors[`${prefix}email`],
            control,
            inputProps: {
                fontSize: 'sm',
                lineHeight: '18px'
            }
        },

        otp: {
            name: `${prefix}otp`,
            placeholder: formatMessage({
                defaultMessage: 'One-Time Password*',
                id: 'use_update_password_fields.placeholder.otp'
            }),
            defaultValue: '',
            type: 'password',
            autoComplete: 'otp',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Enter the temporary password sent to you.',
                    id: 'use_update_password_fields.error.required_otp'
                })
            },
            error: errors[`${prefix}otp`],
            control,
            inputProps: {
                fontSize: 'sm',
                lineHeight: '18px'
            }
        },

        password: {
            name: `${prefix}password`,
            placeholder: formatMessage({
                defaultMessage: 'New Password*',
                id: 'use_update_password_fields.label.new_password'
            }),
            type: 'password',
            autoComplete: 'new-password',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Enter a new password.',
                    id: 'use_update_password_fields.error.required_new_password'
                }),
                validate: {
                    hasMinChars: (val) =>
                        validatePassword(val).hasMinChars ||
                        formatMessage({
                            defaultMessage: 'Password must contain at least 8 characters.',
                            id: 'use_update_password_fields.error.minimum_characters'
                        }),
                    hasUppercase: (val) =>
                        validatePassword(val).hasUppercase ||
                        formatMessage({
                            defaultMessage: 'Password must contain at least one uppercase letter.',
                            id: 'use_update_password_fields.error.uppercase_letter'
                        }),
                    hasLowercase: (val) =>
                        validatePassword(val).hasLowercase ||
                        formatMessage({
                            defaultMessage: 'Password must contain at least one lowercase letter.',
                            id: 'use_update_password_fields.error.lowercase_letter'
                        }),
                    hasNumber: (val) =>
                        validatePassword(val).hasNumber ||
                        formatMessage({
                            defaultMessage: 'Password must contain at least one number.',
                            id: 'use_update_password_fields.error.contain_number'
                        }),
                    hasSpecialChar: (val) =>
                        validatePassword(val).hasSpecialChar ||
                        formatMessage({
                            defaultMessage: 'Password must contain at least one special character.',
                            id: 'use_update_password_fields.error.special_character'
                        })
                }
            },
            error: errors[`${prefix}password`],
            control,
            inputProps: {
                fontSize: 'sm',
                lineHeight: '18px'
            }
        },

        passwordConfirm: {
            name: `${prefix}passwordConfirm`,
            placeholder: formatMessage({
                defaultMessage: 'Confirm Password*',
                id: 'use_update_password_fields.placeholder.new_password_confirm'
            }),
            type: 'password',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Enter your password again.',
                    id: 'use_update_password_fields.error.required_confirm_password'
                }),
                validate: (value, formValues) =>
                    value === formValues?.[`${prefix}password`] ||
                    formatMessage({
                        defaultMessage: 'Does not match new password.',
                        id: 'use_update_password_fields.error.passwords_do_not_match'
                    })
            },
            error: errors[`${prefix}passwordConfirm`],
            control,
            variant: 'floating',
            inputProps: {
                fontSize: 'sm',
                lineHeight: '18px'
            }
        }
    }

    return fields
}
